import type React from 'react';
import { isBefore, parseISO } from 'date-fns';
import { type FieldApi, type ValidateResult, type AnyMeta, useFormContext } from 'react-form';
import type {
  CustomPageSection,
  RestaurantLocationAnsweringSetting,
  RestaurantLocationAnsweringSettingFallbackPhoneNumberInput,
} from '~/libs/gql/types';
import { toFloat, toInt } from './numbers';
import { isValidSignupPhoneFormat } from './phoneSignupValidation';

export const VALIDATION_TIMEOUT = 500;
const DOORDASH_DELIVERY_FEE = 6.99;
const DOORDASH_CANADA_DELIVERY_FEE = 8.5;

type SyncValidator<TFieldValue = string> = (value: TFieldValue, instance: FieldApi<TFieldValue>) => ValidateResult;
export type Validator<TFieldValue = string> = (value: TFieldValue, instance: FieldApi<TFieldValue>) => ValidateResult | Promise<ValidateResult>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Using `unknown` doesn't work as desired here
export const blank = (value: any): ValidateResult => {
  if (value === undefined || value === null || value === 'null' || String(value).trim().length === 0) {
    return 'Cannot be blank';
  }
  return false;
};

const numMatches = (string: string, regex: RegExp) => string.match(regex)?.length ?? 0;

const validLiquidSyntax = (message: string): ValidateResult => {
  if (!message) {
    return false;
  }

  const openBraces = numMatches(message, /{{/g);
  const closeBraces = numMatches(message, /}}/g);

  return openBraces !== closeBraces ? 'opening braces {{ must have matching closing braces }}' : false;
};

export const validEmail = (email: string): ValidateResult => {
  // eslint-disable-next-line no-useless-escape
  if (!email || /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g.test(email)) {
    return false;
  } else {
    return 'must be a valid email (e.g., user@example.com).';
  }
};

export const validCheckoutEmail = (email: string): ValidateResult => {
  // eslint-disable-next-line no-useless-escape
  if (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g.test(email)) {
    return false;
  } else {
    return 'address is invalid.';
  }
};

export const validPhone = (phone: string): ValidateResult => {
  if (!blank(phone)) {
    const northAmericanRegex = /^(\+?1[\s.-]?)?[(]?[2-9]{1}\d{2}[)]?[\s.-]?[2-9]{1}\d{2}[\s.-]?\d{4}$/;

    // UK formats:
    // - International: 44 20 7946 0958, 442079460958
    const ukRegex = /^(\+?44\s?)((\d{10,11})|(\d{2}\s\d{4}\s\d{4})|(\d{5}\s\d{6}))$/;
    if (!northAmericanRegex.test(phone) && !ukRegex.test(phone)) {
      return 'is invalid (us/uk phone number format accepted, including +1)';
    }
  }
  return false;
};

export const validSignupPhone = (phone: string): ValidateResult => (isValidSignupPhoneFormat(phone) ? false : 'must be a valid phone number.');

export const validUrl = (value: string): ValidateResult => {
  if (!blank(value) && value.length > 0) {
    const phoneRegex = /^(tel:)/;
    const mailtoRegex = /^(mailto:)/;
    const uriRegex = /^(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.[^\s]{2,}$/;
    const draftRegex = /draft=true/g;
    if (!phoneRegex.test(value) && !mailtoRegex.test(value) && !uriRegex.test(value) || draftRegex.test(value)) {
      return 'does not match desired format';
    }
  }
  return false;
};

const htmlTag = (value: string): ValidateResult => {
  const content = value || '';
  const attributeCount = numMatches(content, /\w=("|')(.*?)("|')/g);
  const openingAttributeCount = numMatches(content, /\w(="|=')/g);
  if (openingAttributeCount !== attributeCount) {
    return 'Invalid HTML attributes';
  }

  const openingTagCount = numMatches(content, /<(?!area|br|embed|hr|img|input|link|meta|source|wbr)(\w[^>]*)>/g);
  const closingTagCount = numMatches(content, /<\/\w*>/g);
  if (openingTagCount !== closingTagCount) {
    return 'Unclosed HTML tags';
  }
  return false;
};

const iFrameTag = (value: string): ValidateResult => {
  const content = value || '';
  const iFrameCount = numMatches(content, /<iframe/g);
  const titleCount = numMatches(content, /title="[A-Za-z0-9$]/g);
  if (titleCount < iFrameCount) {
    return 'All <iframe /> tags must have a title attribute';
  }
  return false;
};

const imageTag = (value: string): ValidateResult => {
  const content = value || '';
  const imageCount = numMatches(content, /<img/g);
  const altTextCount = numMatches(content, /alt="[A-Za-z0-9$]/g);
  if (altTextCount < imageCount) {
    return 'All <img /> tags must have an alt attribute (Click the "T" icon and add a short photo description for the visually impaired)';
  }
  return false;
};

// prevents links created by internal email blocker from being saved
const containsProhibitedUrl = (value: string): ValidateResult => {
  if (value != null && /antigena.com/.test(value)) {
    return 'Sorry, this field contains a prohibited URL';
  }
  return false;
};

export const greaterThan = (value: string, min: number = 0): ValidateResult => {
  if (+value <= min) {
    return `Must be greater than ${min}`;
  }
  return false;
};

const validatorDictionary = {
  email: validEmail,
  liquid: validLiquidSyntax,
  notBlank: blank,
  phone: validPhone,
  url: validUrl,
} as const;

export const htmlValidator: Validator = value => containsProhibitedUrl(value) || htmlTag(value) || imageTag(value) || iFrameTag(value);

const debounce = <TFieldValue = string>(validator: SyncValidator<TFieldValue>): Validator<TFieldValue> => (value, instance) => instance.debounce(() => validator(value, instance), VALIDATION_TIMEOUT);

export const setFieldMetaAndDebounce = <TFieldValue = string>(validator: SyncValidator<TFieldValue>): Validator<TFieldValue> => debounce((value, instance) => {
  const validationResult = validator(value, instance);

  const fieldMeta: AnyMeta = {
    error: validationResult || false,
  };

  instance.form.setFieldMeta(instance.fieldName, fieldMeta);

  return validationResult;
},
);

export const doorDashCanadaSubsidyValidator = /* @__PURE__ */debounce((value) => {
  if (blank(value)) {
    return 'Cannot be blank';
  } else if (parseFloat(value) > DOORDASH_CANADA_DELIVERY_FEE) {
    return 'Out of fee range';
  }
  return false;
});

export const doorDashSubsidyValidator = /* @__PURE__ */debounce((value) => {
  if (blank(value)) {
    return 'Cannot be blank';
  } else if (parseFloat(value) > DOORDASH_DELIVERY_FEE) {
    return 'Out of fee range';
  }
  return false;
});

export const messageContentValidator = /* @__PURE__ */debounce((value) => {
  if (blank(value)) {
    return blank(value);
  } else if (htmlTag(value)) {
    return `has ${htmlTag(value)}`;
  } else if (imageTag(value)) {
    return 'images require alt attributes (add a short photo description for the visually impaired)';
  }
  return false;
});

export const minOneValidator = /* @__PURE__ */debounce((value) => {
  const parsedValue = toInt(value);
  if (typeof parsedValue === 'number' && parsedValue < 1) {
    return 'Requires minimum of 1';
  }
  return false;
});

export const greaterThanOrEqualZero = (value: string): ValidateResult => {
  const parsedValue = toFloat(value);
  if (typeof parsedValue === 'number' && parsedValue < 0) {
    return 'Requires minimum of 0';
  }
  return false;
};

export const greaterThanOrEqualZeroValidator = /* @__PURE__ */debounce(greaterThanOrEqualZero);

export const notBlankValidator = /* @__PURE__ */debounce(blank);

export const gaTrackingIdValidator = /* @__PURE__ */debounce((value) => {
  const blankResult = blank(value);
  if (blankResult) return blankResult;
  return /^(G-[A-Z0-9]+|UA-\d+(-\d+)?)$/i.test(value.trim()) ? false : 'Must be a Google Analytics ID like G-XXXXXXXXXX or UA-XXXXXX-X';
});

export const gtmContainerIdValidator = /* @__PURE__ */debounce((value) => {
  const blankResult = blank(value);
  if (blankResult) return blankResult;
  return /^GTM-[A-Z0-9]+$/i.test(value.trim()) ? false : 'Must be a Google Tag Manager container ID like GTM-XXXXXXX';
});

export const isScheduledAtInPast = (scheduledAt: string | null | undefined): boolean => (
  !!scheduledAt && isBefore(parseISO(scheduledAt), new Date())
);

export const notPastDateValidator = /* @__PURE__ */debounce((value) => {
  const blankResult = blank(value);
  if (blankResult) return blankResult;
  return isScheduledAtInPast(value) ? 'Scheduled time must be in the future.' : false;
});

export const nonNegativeIntegerValidator = (message: string) => debounce<string | number | null | undefined>((value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return false;
  }
  const stringValue = String(value);
  if (stringValue.includes('.') || stringValue.includes(',')) {
    return message;
  }
  const parsedValue = toInt(value);
  if (parsedValue === null || Number.isNaN(parsedValue) || parsedValue < 0) {
    return message;
  }
  return false;
});

export const notBlankAndGreaterThanOrEqualZeroValidator = /* @__PURE__ */debounce((value) => {
  const parsedValue = toFloat(value);
  if (typeof parsedValue === 'number' && parsedValue < 0) {
    return 'Requires minimum of 0';
  } else if (blank(value)) {
    return 'Cannot be blank';
  }
  return false;
});

export const fallbackNumberValidator = (value: string | undefined, answeringSettings: RestaurantLocationAnsweringSetting): ValidateResult => {
  if (!value) {
    return 'Fallback Numbers cannot be blank';
  }
  const answeringPhone = answeringSettings.phone?.match(/\d/g)?.join('');
  const fallbackPhone = value.match(/\d/g)?.join('');
  const answeringPhoneWithPrefix = `1${answeringPhone}`;
  const regex = /^(\()?[2-9]{1}\d{2}(\))?(-|\s)?[2-9]{1}\d{2}(-|\s)\d{4}$/;
  if (!fallbackPhone) {
    return 'Fallback Numbers cannot be blank';
  }

  if (!regex.test(value)) {
    return 'Fallback Number is invalid or does not match desired format.';
  }

  if (answeringPhone === fallbackPhone || answeringPhoneWithPrefix === fallbackPhone) {
    return 'Fallback Numbers cannot be the AI Answering number';
  }

  return false;
};

export const checkForDuplicateFallbacks = (fallbackPhoneNumbers: RestaurantLocationAnsweringSettingFallbackPhoneNumberInput[]) => {
  const seenFallbackTypes = new Set<string | undefined>();
  const seenPhoneNumbers = new Set<Optional<string>>();
  let errorMessage: ValidateResult = false;

  fallbackPhoneNumbers.forEach(({ fallbackType, phoneNumber }) => {
    const lowercaseFallbackType = fallbackType?.toLowerCase();

    if (seenFallbackTypes.has(lowercaseFallbackType)) {
      errorMessage = ('answering.settings.error.duplicate_fallback_type');
      return;
    }
    seenFallbackTypes.add(lowercaseFallbackType);

    if (seenPhoneNumbers.has(phoneNumber)) {
      errorMessage = ('answering.settings.error.duplicate_phone_number');
      return;
    }
    seenPhoneNumbers.add(phoneNumber);
  });
  return errorMessage;
};

export const textLengthValidatorAndNotBlank = ({ max }: { max: number }) => (
  debounce((value) => {
    if (blank(value)) {
      return blank(value);
    }

    if (max < value.length) {
      let result = `must be not more than ${max}`;
      result += ' characters in length.';
      return result;
    }
    return false;
  })
);

export const giftCardImageValidator = (values: CustomPageSection) => {
  const isUnique = values.giftCardImages.reduce<Record<number, true> | false>((c, el) => {
    const cache = c;
    const imageId = el?.imageUploadedPhoto?.id;
    if (imageId === undefined) return cache;
    if (!cache) return false;
    if (cache[imageId]) return false;
    cache[imageId] = true;
    return cache;
  }, {});
  return isUnique ? false : 'Gift Card images must be unique';
};

export const locationSearchValidator = (values: CustomPageSection) => {
  const locationsId = values.selectedLocations.filter(({ locationId }) => locationId != null).map(({ locationId }) => locationId);
  const isUnique = (new Set(locationsId)).size === locationsId.length;
  return isUnique ? false : 'Locations must be unique';
};

export const greaterThanAndNotBlankValidator = (limit: number) => debounce((value) => {
  if (blank(value)) {
    return blank(value);
  } else if (greaterThan(value, limit)) {
    return greaterThan(value, limit);
  }
  return false;
});

export const positiveIntegerAndNotBlankValidator = /* @__PURE__ */debounce((value) => {
  if (blank(value)) {
    return 'Cannot be blank';
  }

  const stringValue = String(value);
  if (stringValue.includes('.') || stringValue.includes(',')) {
    return 'Must be a whole number';
  }
  const parsedValue = toInt(value);
  if (Number.isNaN(parsedValue) || parsedValue === null || parsedValue < 1) {
    return 'Must be greater than 0';
  }
  return false;
});

interface Limits {
  min?: number;
  max?: number;
}

export const textLengthValidator = ({ min = 0, max }: Limits) => (
  debounce((value) => {
    if (!value) {
      return false;
    }

    if (min > value.length || (max != null && max < value.length)) {
      let result = min > 0 ? `must be between ${min} and ${max}` : `must be not more than ${max}`;
      result += ' characters in length.';
      return result;
    }
    return false;
  })
);

export const textLengthValidatorSync = ({ min = 0, max }: Limits) => (value: string) => {
  if (!value) {
    return false;
  }

  if (min > value.length || (max != null && max < value.length)) {
    let result = min > 0 ? `must be between ${min} and ${max}` : `must be not more than ${max}`;
    result += ' characters in length.';
    return result;
  }
  return false;
};

export const validZipCode = () => (value: string) => {
  const max = 15;

  if (!value) {
    return false;
  }

  if (value.length > max) {
    return `is invalid, ensure using your country's format (numbers, letters, or both), with a maximum ${max} characters in length.`;
  }
  return false;
};

export const isTextLengthLessThan = (text: string | undefined, max: number | undefined) => !text || (max == null || max >= text.length);

export const formatCardExpiryDate = (event: React.ChangeEvent<HTMLInputElement>) => {
  const eventData = event.nativeEvent && (event.nativeEvent as InputEvent).data;
  const prevExpiry = event.target.value.split(' / ').join('/');

  if (!prevExpiry) return null;
  let expiry = prevExpiry;
  if (/^[2-9]$/.test(expiry)) {
    expiry = `0${expiry}`;
  }

  if (prevExpiry.length === 2 && +prevExpiry > 12) {
    const [head, ...tail] = prevExpiry.split('');
    expiry = `0${head}/${tail.join('')}`;
  }

  if (/^1[/-]$/.test(expiry)) {
    return '01 / ';
  }

  const expiryMatches = expiry.match(/(\d{1,2})/g) || [];
  if (expiryMatches.length === 1) {
    if (!eventData && prevExpiry.includes('/')) {
      return expiryMatches[0];
    }
    if (/\d{2}/.test(expiry)) {
      return `${expiryMatches[0]} / `;
    }
  }
  if (expiryMatches.length > 2) {
    const [, month = null, year = null] = expiryMatches.join('').match(/^(\d{2}).*(\d{2})$/) || [];
    return [month, year].join(' / ');
  }
  return expiryMatches.join(' / ');
};

export const usStatesList = () => ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'Washington DC', 'West Virginia', 'Wisconsin', 'Wyoming'];

export const samePasswordValidator = /* @__PURE__ */debounce((value, instance) => {
  if (blank(value)) {
    return blank(value);
  } else if (value === instance.form.values.password) {
    return false;
  } else {
    return ' does not match.';
  }
});

export const passwordValidator = /* @__PURE__ */debounce((value) => {
  if (blank(value)) {
    return blank(value);
  } else {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?!.*[Pp][Oo][Pp][Mm][Ee][Nn][Uu])(?=.*[@#$!%^*?&\-_=+`~[\]{}():;<>/.,'"])[A-Za-z\d@#$!%^*?&\-_=+`~[\]{}():;<>/.,'"]{8,}$/;
    if (!passwordRegex.test(value)) {
      return ' does not meet minimum requirements.';
    }
  }
  return false;
});

export const phoneValidator = /* @__PURE__ */debounce((value) => {
  if (!blank(value)) {
    const northAmericanRegex = /^(\+?1[\s.-]?)?[(]?[2-9]{1}\d{2}[)]?[\s.-]?[2-9]{1}\d{2}[\s.-]?\d{4}$/;

    const ukRegex = /^(\+?44\s?)(\s?\d{3,5}|\(\d{3,5}\))(\s?\d{3,4})(\s?\d{3,4})$/;

    if (!northAmericanRegex.test(value) && !ukRegex.test(value)) {
      return 'is invalid (us/uk phone number format accepted, including +1)';
    }
  }
  return false;
});

export const urlValidator = /* @__PURE__ */debounce(validUrl);

export const multiValidator = (validators: (SyncValidator | keyof typeof validatorDictionary)[]): Validator => setFieldMetaAndDebounce((value, instance) => {
  let validationResult: ValidateResult | Promise<ValidateResult> = false;
  for (const validator of validators) {
    const resolvedValidator: SyncValidator = typeof validator === 'function' ?
      validator :
      validatorDictionary[validator];

    const result = resolvedValidator(value, instance);

    if (result !== false) {
      validationResult = result;
      break;
    }
  }
  return validationResult;
});

export const isPositiveNumber = (value: string) => {
  const validNumber = new RegExp(/^\d*\.?\d*$/);

  return validNumber.test(value);
};

export const numberValidator = (value: string, message: string) => {
  if (isPositiveNumber(value)) {
    return false;
  }

  return message;
};

export interface CustomImageElement extends HTMLImageElement {
  metadata: {
    bytes: number;
  };
}

export const imageSizeInBytesValidator = (image: CustomImageElement): string | false => {
  if (!image) {
    return false;
  }

  const minAllowedBytes = 10 * 1024; // 10 KB in bytes
  const maxAllowedBytes = 5 * 1024 * 1024; // 5 MB in bytes
  const { bytes } = image.metadata;

  if (bytes < minAllowedBytes) {
    return "Image is too small, per Google's guidelines. Minimum size: 10KB";
  }

  if (bytes > maxAllowedBytes) {
    return "Image is too big, per Google's guidelines. Max size: 5MB";
  }

  return false;
};

export const useFormContextUnderFieldScope = useFormContext as unknown as () => FieldApi<unknown>;

// Used for the base dish details settings. Only two out of the three tabs can be enabled at a time.
export const hasTwoDishDetailsTabsEnabled = (values: Record<string, unknown>, field: string) => {
  switch (field) {
    case 'reservations':
      return values.showWaitlistTab && values.showCustomTab;
    case 'custom':
      return values.showReservationsTab && values.showWaitlistTab;
    case 'waitlist':
      return values.showReservationsTab && values.showCustomTab;
    default:
      return false;
  }
};
